import React, { useCallback, useContext, useEffect, useState } from 'react'
import { CorporateActionDto } from '@/api/Trading-Accounts/Types/CorporateActionDto'
import { systemCorporateActionFields } from '@/components/Filter/systemCorporateActionFields'
import { successToast, ToastContext } from '@/components/Toast/Toast'
import { Breadcrumbs } from '@/components/ui/breadcrumb'
import { formatLocalDateUTC } from '@/utils/date'
import { SortDescriptor } from '@progress/kendo-data-query'
import {
  GridColumn as Column,
  Grid,
  GridCellProps,
  GridSortChangeEvent,
} from '@progress/kendo-react-grid'
import { CreateFilter } from 'api/Identity/Types/CreateFilter'
import { PageData } from 'api/Pagination/Types'
import { Pager } from 'components/components/Pager'
import { Modal } from 'components/CustomModal'
import { FilterType } from 'components/Filter'
import { SelectFiltersFacade } from 'components/Filter/ClientFields/SelectFiltersFacade'
import { createGridState, useGridFilter, useGridState, useGridStatusState } from 'features/DataGrid'
import { isGoBack } from 'lib/react-router-dom'
import { useAppDispatch, useAppSelector } from 'store'
import {
  CompositeFilterDescriptor,
  GridFilterActionState,
  selectGridFilterByFilterId,
} from 'store/grid'
import {
  fetchFilterAction,
  fetchFilterTypesAction,
  removeFilterAction,
  saveFilterAction,
  updateFilterAction,
} from 'store/identity/identity.actions'
import {
  fetchInstrumentsAction,
  getCorporateActionTypes,
  getCurrenciesAction,
} from 'store/trading-accounts/trading-accounts.actions'
import { PageQuery } from 'utils/api'
import { useApiClient } from 'utils/api/ApiClient'
import { IdentityApiClient } from 'utils/api/identityApi'
import { TradingAccountsApiClient } from 'utils/api/tradingAccountsApi'
import { links } from 'utils/featureToggles'
import { isEmpty } from 'utils/is-empty'
import { buildQuery, buildSortQuery } from 'utils/QueryHelpers'
import { TableWithExport } from 'utils/table/withExport'
import { TableStateContext, TableStateContextData } from 'utils/TableStateContext'

import MoreButton from '../../../components/MoreButton/MoreButton'
import { CustomColumnMenu } from '../../Records/Clients/CustomColumnMenu'
import { GridColumn } from './dividendsColumn'
import { DividendsTab } from './DividendsNavigation'
import {
  EditCorporateActionFormValues,
  EditSystemCorporateActionForm,
} from './EditSystemCorporateActionForm'
import { normalizeDividendSortQuery } from './normalizers/normalizeDividendSortQuery'
import systemCustomCorporateActionColumns from './systemCorporateActionsColumn'

const useMapStateToProps = () => ({
  filters: useAppSelector((state) => state.identity.filter),
  filterTypes: useAppSelector((state) => state.identity.filterType),
  gridFilterByFilterIdSelector: useAppSelector(selectGridFilterByFilterId),
})

const useMapDispatchToProps = () => {
  const dispatch = useAppDispatch()

  const identityApiClient = useApiClient(IdentityApiClient)
  return {
    fetchFilterTypes: async () => dispatch(fetchFilterTypesAction(identityApiClient)),
    fetchFilters: async () => dispatch(fetchFilterAction(identityApiClient)),
    saveFilter: async (data: CreateFilter) => dispatch(saveFilterAction(data, identityApiClient)),
    updateFilter: async (filterId: string, data: CreateFilter) =>
      dispatch(updateFilterAction(filterId, data, identityApiClient)),
    removeFilter: async (id: string) => dispatch(removeFilterAction(id, identityApiClient)),
  }
}

const SystemCorporateActionPage = (): JSX.Element => {
  const dispatch = useAppDispatch()

  const [isEditCorporateAction, setIsEditCorporateAction] = useState<{
    data: CorporateActionDto | undefined
    visible: boolean
  }>({
    data: undefined,
    visible: false,
  })
  const setToast = useContext(ToastContext)

  const [columns, setColumns] = useState<GridColumn[]>(systemCustomCorporateActionColumns)
  const [corporateActions, setCorporateActions] = useState<CorporateActionDto[]>([])
  const [tableData, setTableData] = useState<TableStateContextData>({ skipCount: true })
  const [moreModalVisible, setMoreModalVisible] = useState<CorporateActionDto | undefined>()

  const { gridStatusState, gridStatusActions } = useGridStatusState()
  const { gridState, gridActions } = useGridState()
  const { selectFilterForGoBackHistory } = useGridFilter(gridStatusActions)

  const tradingAccountsApiClient = useApiClient(TradingAccountsApiClient)

  const dividendCurrencies = useAppSelector((state) => state.tradingAccounts.currencies)
  const brokerSymbols = useAppSelector((state) => state.tradingAccounts.instruments)
  const corporateActionTypes = useAppSelector((state) => state.tradingAccounts.corporateActionTypes)

  const { filters, filterTypes } = useMapStateToProps()

  const { fetchFilterTypes, fetchFilters } = useMapDispatchToProps()

  useEffect(() => {
    if (isEmpty(brokerSymbols)) dispatch(fetchInstrumentsAction(tradingAccountsApiClient))
    if (isEmpty(dividendCurrencies)) dispatch(getCurrenciesAction(tradingAccountsApiClient))
    if (isEmpty(corporateActionTypes)) dispatch(getCorporateActionTypes(tradingAccountsApiClient))
  }, [])

  const fetchGrid = async (
    callback: (gridFilterState: GridFilterActionState) => Promise<PageData<CorporateActionDto[]>>
  ) => {
    gridStatusActions.setLoading(true)

    const gridFilterState = selectFilterForGoBackHistory(
      createGridState({ skip: 0 }, gridState, { isGoBack })
    )
    const response = await callback(gridFilterState)
    if (response?.totalResults >= 0) {
      const total = response.totalResults

      gridActions.setGridFilters({
        total,
      })
    }

    gridStatusActions.setLoading(false)
  }

  const handleFetchGrid = async () => {
    await fetchGrid(
      async (gridFilterState: GridFilterActionState): Promise<PageData<CorporateActionDto[]>> => {
        const searchQuery = getSearchQuery(gridFilterState.filter)
        const sortQuery = getSort(gridFilterState.sort)
        const response = await tradingAccountsApiClient.getCorporateActions({
          search: searchQuery,
          sort: sortQuery,
          pageIndex: gridFilterState.pageIndex,
          pageSize: gridFilterState.pageSize,
          skipCount: tableData?.skipCount !== false,
        })

        if (isEmpty(filterTypes)) await fetchFilterTypes()
        if (isEmpty(filters)) await fetchFilters()

        setColumns(systemCustomCorporateActionColumns)
        if (response?.items) {
          setCorporateActions(response.items)
        }
        setTableData({ ...tableData, meta: response })

        return response
      }
    )
  }

  useEffect(() => {
    handleFetchGrid()
  }, [])

  const getSearchQuery = useCallback(
    (filter?: CompositeFilterDescriptor) => {
      return buildQuery(filter ?? gridState.filter) ?? null
    },
    [gridState.filter]
  )

  const getSort = useCallback(
    (sortDescriptors?: SortDescriptor[]): string | null => {
      return buildSortQuery(normalizeDividendSortQuery(sortDescriptors ?? gridState.sort))
    },
    [gridState.sort]
  )

  const filterFetch = useCallback(
    async (query?: PageQuery): Promise<void> => {
      const searchQuery = query?.search !== undefined ? query.search : getSearchQuery()
      const sortQuery = query?.sort ?? getSort(gridState.sort)
      const pageIndex = query?.pageIndex ?? gridState.pageIndex ?? 1
      const pageSize = query?.pageSize ?? gridState.pageSize ?? 10
      const take = pageSize
      const skip = (pageIndex - 1) * pageSize
      const response = await tradingAccountsApiClient.getCorporateActions({
        search: searchQuery,
        sort: sortQuery,
        pageIndex,
        pageSize,
        skipCount: tableData?.skipCount !== false,
      })

      const total = response.totalResults

      if (response?.items) {
        setCorporateActions(response.items)
      }
      gridActions.setGridFilters({
        take,
        skip,
        total,
        pageIndex,
      })
      setTableData({ ...tableData, meta: response })
    },
    [
      getSearchQuery,
      getSort,
      gridActions,
      gridState.pageSize,
      gridState.sort,
      gridState.take,
      tableData?.skipCount,
    ]
  )

  const handleColumnsSubmit = (columnsState: GridColumn[]) => {
    setColumns(columnsState)
  }

  const handleSortChange = async (event: GridSortChangeEvent) => {
    gridActions.setGridFilters({
      sort: event.sort,
    })

    gridStatusActions.setFetching(true)
    const searchQuery = getSearchQuery(gridState.filter)
    const sortQuery = getSort(event.sort)
    const response = await tradingAccountsApiClient.getCorporateActions({
      search: searchQuery,
      sort: sortQuery,
      pageIndex: gridState.pageIndex,
      pageSize: gridState.take,
      skipCount: tableData?.skipCount !== false,
    })

    gridStatusActions.setFetching(false)

    if (response?.items) {
      setCorporateActions(response.items)
    }
    setTableData({ ...tableData, meta: response })
  }

  const handleSkipCountChange = async (skipCount: boolean) => {
    gridStatusActions.setFetching(true)
    const searchQuery = getSearchQuery()
    const sortQuery = getSort(gridState.sort)

    const response = await tradingAccountsApiClient.getCorporateActions({
      search: searchQuery,
      sort: sortQuery,
      pageIndex: gridState.pageIndex,
      pageSize: gridState.take,
      skipCount,
    })
    const total = response.totalResults

    gridStatusActions.setFetching(false)

    if (response?.items) {
      setCorporateActions(response.items)
    }
    gridActions.setGridFilters({
      total,
    })
    setTableData({ ...tableData, skipCount, meta: response })
  }

  const handleGridPageChange = async (page: number) => {
    gridStatusActions.setFetching(true)
    const searchQuery = getSearchQuery()
    const sortQuery = getSort(gridState.sort)
    const pageIndex = page
    gridActions.setGridFilters({
      pageIndex,
    })

    const response = await tradingAccountsApiClient.getCorporateActions({
      search: searchQuery,
      sort: sortQuery,
      pageIndex,
      pageSize: gridState.take,
      skipCount: tableData?.skipCount !== false,
    })
    const total = response.totalResults

    gridStatusActions.setFetching(false)

    gridActions.setGridFilters({
      total,
    })
    if (response?.items) {
      setCorporateActions(response.items)
    }
    setTableData({ ...tableData, meta: response })
  }

  const handlePageSizeChange = async (pageSize: number) => {
    gridStatusActions.setFetching(true)
    await gridActions.setGridFilters({
      ...gridState,
      pageSize,
    })

    const searchQuery = getSearchQuery()
    const sortQuery = getSort(gridState.sort)

    const response = await tradingAccountsApiClient.getCorporateActions({
      search: searchQuery,
      sort: sortQuery,
      pageIndex: 1,
      pageSize,
      skipCount: tableData?.skipCount !== false,
    })
    const total = response.totalResults

    gridStatusActions.setFetching(false)

    gridActions.setGridFilters({
      ...gridState,
      pageIndex: 1,
      take: pageSize,
      total,
    })
    if (response?.items) {
      setCorporateActions(response.items)
    }
    setTableData({ ...tableData, meta: response })
  }

  const handleEdit = (dividend: CorporateActionDto) => {
    setIsEditCorporateAction({ visible: true, data: dividend })
  }

  const handleEditCancel = () => {
    setIsEditCorporateAction({ data: undefined, visible: false })
  }

  const onSubmit = async (values: EditCorporateActionFormValues) => {
    await tradingAccountsApiClient.updateCorporateAction(values.id, values)
    setToast(successToast(`Action ${values.id} has been updated.`))
    handleEditCancel()
    await filterFetch()
  }

  return (
    <React.Fragment>
      <Breadcrumbs
        crumbs={[
          {
            title: 'Brokerage - System Corporate Events',
            link: links.brokerageDividends.url.replace(':slug', DividendsTab.SystemCorporate),
          },
        ]}
      />
      <SelectFiltersFacade
        gridState={gridState}
        gridStatusActions={gridStatusActions}
        gridStatusState={gridStatusState}
        setGridFilters={gridActions.setGridFilters}
        filterFetch={filterFetch}
        filterType={FilterType.SystematicCorporateActionsOverview}
        filterFields={systemCorporateActionFields()}
        containerClassName='dividends-table__filter'
        getQuery={getSearchQuery}
      />
      <TableStateContext.Provider value={[tableData, setTableData]}>
        <TableWithExport data={corporateActions} columns={columns}>
          <Grid
            {...gridState}
            resizable
            data={corporateActions}
            sort={gridState.sort}
            total={gridState.total}
            take={gridState.take}
            skip={gridState.skip}
            onSortChange={handleSortChange}
            pageable
            pager={() => (
              <Pager
                onSkipCountChange={handleSkipCountChange}
                onPageChange={handleGridPageChange}
                onPageSizeChange={handlePageSizeChange}
                page={gridState.pageIndex}
                pageSize={gridState.pageSize}
                take={gridState.take || 10}
                skip={gridState.skip || 0}
              />
            )}
            style={{ zIndex: 0 }}
          >
            <Column
              width={140}
              field='date'
              cell={(props) => (
                <td className='table-columns__date'>
                  {formatLocalDateUTC(props.dataItem.date, true)}
                </td>
              )}
              title='Date'
            />
            <Column
              field='externalId'
              cell={(props) => (
                <td
                  onClick={() => setMoreModalVisible(props.dataItem as CorporateActionDto)}
                  className='table-columns__link'
                >
                  {props.dataItem.externalId}
                </td>
              )}
              title='External Id'
            />
            {columns
              .filter((c) => c.show)
              .map((column, idx) => (
                <Column
                  key={idx + 2}
                  {...column}
                  columnMenu={(props) => (
                    <CustomColumnMenu
                      {...props}
                      columns={columns}
                      onColumnsSubmit={handleColumnsSubmit}
                    />
                  )}
                />
              ))}
            <Column
              width={50}
              title=''
              cell={(props: GridCellProps) => (
                <td>
                  <MoreButton
                    items={[
                      {
                        name: 'Edit',
                        'data-test': 'edit',
                        onClick: () => handleEdit(props.dataItem),
                      },
                    ]}
                    data-test='more-button'
                  />
                </td>
              )}
            />
          </Grid>
        </TableWithExport>
      </TableStateContext.Provider>
      {moreModalVisible && (
        <Modal visible={!!moreModalVisible} onClose={() => setMoreModalVisible(undefined)}>
          <Grid
            resizable
            data={Object.keys(moreModalVisible.details).map((k: string) => ({
              field: k,
              value: moreModalVisible?.details[k],
            }))}
          ></Grid>
        </Modal>
      )}
      {isEditCorporateAction.visible && isEditCorporateAction.data && (
        <Modal visible={isEditCorporateAction.visible} onClose={handleEditCancel}>
          <EditSystemCorporateActionForm data={isEditCorporateAction.data} onSubmit={onSubmit} />
        </Modal>
      )}
    </React.Fragment>
  )
}

export default SystemCorporateActionPage
